import { Dispatch, useCallback, useMemo, useState, MouseEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';

import { IKnmErknmFormSubject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import styles from './KnmEpguNotificationItem.module.scss';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmState } from '../../MainModule/types/IKnmState';
import { getEpguNotificationList, getEpguNotificationTotalCount } from '../../MainModule/selectors/knmSelectors';
import { IEpguNotification } from '../../../APIPackage/KnmModule/interfaces/IEpguNotification';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { knmInfoGetEpguNotificationList } from '../../MainModule/actions/knmActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { mapEpguStatus } from '../common/mapEpguStatus';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';

interface IKnmEpguNotificationItemProps {
  organization: IKnmErknmFormSubject;
  showDetailsModal: (e: MouseEvent, s: string[]) => void;
  closeDetailsModal: () => void;
  isShowDetailsModal: boolean;
  type: string;
}

interface IKnmEpguNotificationItemStateProps {
  epguNotificationList: IKnmState['epguNotificationList'] | undefined;
  epguNotificationTotalCount: IKnmState['epguNotificationTotalCount'] | undefined;
}

const KnmEpguNotificationItemWithoutStore = ({
  organization,
  epguNotificationList,
  epguNotificationTotalCount,
  showDetailsModal,
  closeDetailsModal,
  isShowDetailsModal,
  type,
}: IKnmEpguNotificationItemProps & IKnmEpguNotificationItemStateProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isOpened, setIsOpened] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const notifications: IEpguNotification[] = useMemo(() => {
    if (organization.guid && epguNotificationList) {
      return epguNotificationList[organization.guid] || [];
    }

    return [];
  }, [organization, epguNotificationList]);

  const totalCount: number | undefined = useMemo(
    () => (organization.guid ? epguNotificationTotalCount?.[organization.guid] : undefined),
    [organization, epguNotificationTotalCount],
  );

  const handleSelectRow = useCallback(
    (index: number, structure: string[], event: MouseEvent<HTMLDivElement>, eventName: string) => {
      setActiveIndex(index);
      if (eventName.indexOf('Выдача предписания') === -1) {
        showDetailsModal(event, structure);
      } else {
        closeDetailsModal();
      }
    },
    [setActiveIndex, showDetailsModal],
  );

  const handleLoad = useCallback(() => {
    organization.guid && dispatch(knmInfoGetEpguNotificationList(organization.guid, type));
  }, [organization, type]);

  const onToggle = useCallback(() => {
    !isOpened && totalCount === undefined && handleLoad();
    setIsOpened(!isOpened);
    closeDetailsModal();
  }, [setIsOpened, isOpened, totalCount, handleLoad, closeDetailsModal]);

  const convertDate = (value: string | undefined | null): string | undefined =>
    convertInitialKnmDateToFormDate(value, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS);

  // @ts-ignore
  const orgName = organization.organizationName || organization.originalName || '';

  return (
    <KnmCollapse title={`Контролируемое лицо: ${orgName}`} opened={isOpened} onToggle={onToggle}>
      <div className={styles.Table}>
        <div className={styles.TableHeader}>
          <div>СОБЫТИЕ</div>
          <div>ДАТА ПЕРЕДАЧИ ДАННЫХ В ЕПГУ</div>
          <div>ДАТА ИНФОРМИРОВАНИЯ КОНТРОЛИРУЕМОГО ЛИЦА</div>
          <div>СТАТУС</div>
        </div>
        {notifications.length === 0 ? (
          <div className={styles.TableBodyEmpty}>
            <span>Контролируемое лицо не уведомлялось по данному мероприятию</span>
          </div>
        ) : (
          <div className={styles.TableBody}>
            <div className={styles.TableContent}>
              {notifications.map((item, index) => (
                <div
                  key={`epguNotificationItem[${index}]`}
                  className={cn(styles.TableRow, {
                    [styles.TableRowActive]: isShowDetailsModal && activeIndex === index,
                  })}
                  onClick={event => handleSelectRow(index, item.structure, event, item.eventName)}
                >
                  <div>{item.eventName}</div>
                  <div>{convertDate(item.epguSendDate)}</div>
                  <div>{convertDate(item.epguPlacementDate)}</div>
                  <div>{mapEpguStatus(item.status)}</div>
                </div>
              ))}
            </div>
            {totalCount !== undefined && notifications.length < totalCount && (
              <Button
                className={styles.LoadButton}
                type="button"
                text="Показать еще"
                color="load"
                onClick={handleLoad}
              />
            )}
          </div>
        )}
      </div>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IKnmEpguNotificationItemStateProps => ({
  epguNotificationList: getEpguNotificationList(state),
  epguNotificationTotalCount: getEpguNotificationTotalCount(state),
});

export const KnmEpguNotificationItem = connect(mapStateToProps)(KnmEpguNotificationItemWithoutStore);
